import React, { useEffect, useState } from 'react'
import { Controller, Control, UseFormSetValue } from 'react-hook-form'
import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file
import { enUS, fr, de, it, es, nl, pt } from 'date-fns/locale'

import { FormControl, Stack, InputAdornment, Grid } from '@mui/material'
// @ts-expect-error - Import needs esModuleInterop flag
import { useTranslation } from '@contexts/translation'
import { DateRange, RangeKeyDict } from 'react-date-range'
import CustomPopover from '@components/common/Popover'
import TextFieldItem from '@components/common/Form/TextFieldItem'
import { CalendarToday } from '@mui/icons-material'
import { format } from 'date-fns'

// Import locale type from date-fns
import type { Locale } from 'date-fns'

// Define locale type with placeholder
interface LocaleInfo {
  locale: Locale
  placeholder: string
}

interface DateRangePickerCustomProps {
  /** React Hook Form control object */
  control: Control<any>
  /** Whether the field takes up the full width */
  fullWidth?: boolean
  /** Whether the field is required */
  required?: boolean
  /** Custom width for the field */
  width?: string
  /** Start date in format DD-MM-YYYY */
  start_date?: string | null
  /** End date in format DD-MM-YYYY */
  end_date?: string | null
  /** Margin size */
  margin?: 'none' | 'dense' | 'normal'
  /** Form control size */
  size?: 'small' | 'medium'
  /** React Hook Form setValue function */
  setValue: UseFormSetValue<any>
  /** Whether the fields are disabled */
  disabled?: boolean
  /** Additional props */
  [key: string]: any
}

// Type for date range state
interface DateRangeState {
  startDate: Date
  endDate: Date
  key: string
  color: string
}

// Create a mock placeholder for TextFieldItem to satisfy TypeScript
interface TextFieldItemProps {
  field?: any
  errorMessage?: any
  data_testid: string
  fullWidth?: boolean
  focused?: boolean
  required?: boolean
  disabled?: boolean
  endAdornmentContent?: any
  endAdornmentAction?: string
  endAdornmentClick?: () => any
  selectOnFocus?: boolean
  loading?: boolean
  value?: string
  name?: string
  label?: string
  placeholder?: string
  InputProps?: any
  onClick?: () => void
  [key: string]: any
}

const DateRangePickerCustom = ({
  control,
  fullWidth,
  required,
  width,
  start_date = null,
  end_date = null,
  margin = 'normal',
  size = 'medium',
  setValue,
  disabled = false,
  ...props
}: DateRangePickerCustomProps): JSX.Element => {
  const localeMap: Record<string, LocaleInfo> = {
    en: { locale: enUS, placeholder: 'dd/mm/yyyy' },
    fr: { locale: fr, placeholder: 'jj/mm/aaaa' },
    de: { locale: de, placeholder: 'tt/mm/jjjj' },
    it: { locale: it, placeholder: 'gg/mm/aaaa' },
    es: { locale: es, placeholder: 'dd/mm/aaaa' },
    nl: { locale: nl, placeholder: 'dd/mm/jjjj' },
    pt: { locale: pt, placeholder: 'jj/mm/aaaa' }
  }

  const formatDates = (dateString?: string | null): string | undefined => {
    if (!dateString) return undefined
    const split = dateString.split('-')
    const day = split[0]
    const month = split[1]
    const year = split[2]
    return year + '-' + month + '-' + day
  }

  const { dictionary, lang } = useTranslation()
  const [isDates, setIsDates] = useState<boolean>(false)
  const [endOpen, setEndOpen] = useState<boolean>(false)
  const [state, setState] = useState<DateRangeState[]>([
    {
      startDate: start_date
        ? new Date(formatDates(start_date) || '')
        : new Date(),
      endDate: end_date ? new Date(formatDates(end_date) || '') : new Date(),
      key: 'selection',
      color: '#1AB394'
    }
  ])

  useEffect(() => {
    if (start_date && end_date) {
      setIsDates(true)
      setState([
        {
          startDate: new Date(formatDates(start_date) || ''),
          endDate: new Date(formatDates(end_date) || ''),
          key: 'selection',
          color: '#1AB394'
        }
      ])
    }
  }, [start_date, end_date])

  useEffect(() => {
    if (state[0].startDate && state[0].endDate) {
      setValue('start_date', state[0].startDate)
      setValue('end_date', state[0].endDate)
    }
  }, [state, setValue])

  return (
    <FormControl
      {...props}
      margin={margin}
      size={size}
      style={{ width: fullWidth ? '100%' : width || '195px' }}
    >
      <Stack
        width="100%"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
      >
        <Grid item xs={6}>
          <Controller
            name="start_date"
            control={control}
            render={() => (
              <CustomPopover
                setClosed={() => {}}
                closed={false}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                buttonLabel={
                  <TextFieldItem
                    field={{}}
                    errorMessage=""
                    required={required}
                    disabled={disabled}
                    value={
                      state[0].startDate && isDates
                        ? format(state[0].startDate, 'dd/MM/yyyy')
                        : ''
                    }
                    name="start_date"
                    label={dictionary.start_date}
                    placeholder={localeMap[lang]?.placeholder}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarToday color="primary" />
                        </InputAdornment>
                      )
                    }}
                    data_testid="TextFieldItem-d2c1b9f3-b807-433a-bcc4-f6c76c0e7d6c"
                  />
                }
              >
                <DateRange
                  initialFocusedRange={[0, 0]}
                  editableDateInputs={true}
                  locale={localeMap[lang]?.locale}
                  onChange={(item: RangeKeyDict) => {
                    setState([item.selection as DateRangeState])
                    setIsDates(true)
                  }}
                  moveRangeOnFirstSelection={true}
                  ranges={state}
                  dateDisplayFormat="dd/MM/yyyy"
                  startDatePlaceholder="dd/mm/yyyy"
                  endDatePlaceholder="dd/mm/yyyy"
                />
              </CustomPopover>
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="end_date"
            control={control}
            render={() => (
              <CustomPopover
                setClosed={setEndOpen}
                closed={!endOpen}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                buttonLabel={
                  <TextFieldItem
                    field={{}}
                    errorMessage=""
                    placeholder={localeMap[lang]?.placeholder}
                    disabled={disabled}
                    required={required}
                    onClick={() => setEndOpen(true)}
                    value={
                      state[0].endDate && isDates
                        ? format(state[0].endDate, 'dd/MM/yyyy')
                        : ''
                    }
                    name="end_date"
                    label={dictionary.end_date}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarToday color="primary" />
                        </InputAdornment>
                      )
                    }}
                    data_testid="TextFieldItem-b8de0284-af14-4d7e-8aa3-6b5f1a891966"
                  />
                }
              >
                <DateRange
                  editableDateInputs={true}
                  initialFocusedRange={[0, 1]}
                  locale={localeMap[lang]?.locale}
                  onChange={(item: RangeKeyDict) => {
                    setState([item.selection as DateRangeState])
                    setEndOpen(false)
                  }}
                  moveRangeOnFirstSelection={false}
                  ranges={state}
                  dateDisplayFormat="dd/MM/yyyy"
                  startDatePlaceholder="dd/mm/yyyy"
                  endDatePlaceholder="dd/mm/yyyy"
                />
              </CustomPopover>
            )}
          />
        </Grid>
      </Stack>
    </FormControl>
  )
}

export default DateRangePickerCustom
